import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Loader2, ShieldAlert } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Admin login form schema
const adminLoginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type AdminLoginFormValues = z.infer<typeof adminLoginSchema>;

export default function AdminLoginPage() {
  const { user, isLoading, loginMutation } = useAuth();
  const [loginAttempted, setLoginAttempted] = useState(false);

  const form = useForm<AdminLoginFormValues>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = (values: AdminLoginFormValues) => {
    loginMutation.mutate(values, {
      onSettled: () => {
        setLoginAttempted(true);
      }
    });
  };

  // If user is already logged in and is an admin, redirect to admin dashboard
  if (user && user.role === "admin") {
    return <Redirect to="/admin" />;
  }
  
  // If user is logged in but not an admin, redirect to home
  if (user && user.role !== "admin") {
    return <Redirect to="/" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-2">
            <div className="w-full flex justify-center mb-3">
              <img 
                src="/images/logos/SWaccounting.jpg" 
                alt="SW Accounting Logo" 
                className="h-16 w-auto" 
              />
            </div>
            <CardTitle className="text-2xl font-serif text-center">Administrator Login</CardTitle>
            <CardDescription className="text-center">
              Secure access for SW Accounting administrators
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {loginAttempted && loginMutation.isError && (
              <Alert variant="destructive" className="mb-4">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>Authentication Failed</AlertTitle>
                <AlertDescription>
                  Invalid administrator credentials. Please try again.
                </AlertDescription>
              </Alert>
            )}
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Admin username" 
                          {...field} 
                          autoComplete="username"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Admin password" 
                          {...field} 
                          autoComplete="current-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#1a365d] hover:bg-opacity-90"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-xs text-center text-muted-foreground w-full">
              <p>
                This login is for SW Accounting administrators only.
                <br />
                For client access, please use the client portal.
              </p>
            </div>
          </CardFooter>
        </Card>
        
        <div className="mt-8 text-center">
          <a href="/" className="text-[#7d9b76] hover:underline flex items-center justify-center">
            <i className="fas fa-arrow-left mr-2"></i> Back to Homepage
          </a>
        </div>
      </div>
    </div>
  );
}